import {HttpClientInterface} from "../index";
import {APIResponse, parseItemResponse, parseItemsResponse} from "../ModelFactory";
import {TenantSettings, tenantSettingsFactory} from "./models/TenantSettings";
import {AvailablePreviewDomain, availablePreviewDomainsFactory} from "./models/AvailablePreviewDomain";
import {permissionMapFactory} from "../common/models/PermissionMap";
import {Role, roleFactory, RoleOptions, roleOptionsFactory, RolePermissions} from "./models/Role";

export class CareApiClient {
    constructor(private http: HttpClientInterface) {}

    // Tenant configuration
    public async getTenant(): Promise<APIResponse<TenantSettings>> {
        const json = await this.http.get<{items: object[]}>(`tenant`)
        return parseItemResponse(json, tenantSettingsFactory)
    }
    public async updateTenant(fields: Pick<TenantSettings, "brandTitle"|"brandSubTitle"|"colorPalette">): Promise<void> {
        await this.http.put<{items: object[]}>(`tenant`, {
            brand_title: fields.brandTitle,
            brand_subtitle: fields.brandSubTitle,
            color_palette: fields.colorPalette,
        })
    }
    public async getAvailablePreviewDomains(): Promise<APIResponse<AvailablePreviewDomain[]>> {
        const json = await this.http.get<{items: object[]}>(`preview_domain/options`)
        return parseItemsResponse(json, availablePreviewDomainsFactory)
    }
    public async updatePreviewDomain(domainUuid: string): Promise<void> {
        await this.http.put<Record<string, never>>(`tenant`, {
            site_preview_domain_uuid: domainUuid,
        })
    }
    public async updateCustomerAccess(customersAllowedToOrderDomains: boolean): Promise<void> {
        await this.http.put<Record<string, never>>(`tenant`, {
            customers_allowed_to_order_domains: customersAllowedToOrderDomains,
        })
    }

    // Roles
    public async getRoles(): Promise<APIResponse<Role[]>> {
        const json = await this.http.get<{items: object[]}>(`roles`)
        return parseItemsResponse(json, roleFactory)
    }
    public async getRoleOptions(): Promise<APIResponse<RoleOptions>> {
        const json = await this.http.get<{item: object}>(`roles/options`)
        return parseItemResponse(json, roleOptionsFactory)
    }
    public async getRole(roleUuid: string): Promise<APIResponse<Role>> {
        const json = await this.http.get<{item: object}>(`roles/${roleUuid}`)
        return parseItemResponse(json, roleFactory)
    }
    public async createRole(fields: { displayName: string, name?: string, permissions?: RolePermissions }): Promise<APIResponse<Role>> {
        const json = await this.http.post<{item: object}>(`roles`, {
            display_name: fields.displayName,
            ...(fields.name ? { name: fields.name } : {}),
            ...(fields.permissions ? { permissions: fields.permissions } : {}),
        })
        return parseItemResponse(json, roleFactory)
    }
    public async updateRole(roleUuid: string, fields: { displayName?: string, name?: string, permissions?: RolePermissions }): Promise<APIResponse<Role>> {
        const json = await this.http.put<{item: object}>(`roles/${roleUuid}`, {
            ...(fields.displayName !== undefined ? { display_name: fields.displayName } : {}),
            ...(fields.name ? { name: fields.name } : {}),
            ...(fields.permissions ? { permissions: fields.permissions } : {}),
        })
        return parseItemResponse(json, roleFactory)
    }
    public async deleteRole(roleUuid: string): Promise<{ success: true } | { success: false, error: string }> {
        const json = await this.http.delete<{status?: string, message?: string}>(`roles/${roleUuid}`)
        if (json && typeof json === 'object' && typeof json.message === 'string') {
            return { success: false, error: json.message }
        }
        return { success: true }
    }
    public async setDefaultRole(roleUuid: string): Promise<APIResponse<Role>> {
        const json = await this.http.put<{item: object}>(`roles/${roleUuid}/default`, {})
        return parseItemResponse(json, roleFactory)
    }

    // Permissions
    public async getPermissions() {
        // TODO: Resolve permissions from API
        return parseItemResponse({item: {}}, permissionMapFactory)
    }
}

